import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../css/ProductDetails.css';

const ProductDetails = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [currentImage, setCurrentImage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [ratings, setRatings] = useState([]);
  const navigate = useNavigate();

  // Fetch product details
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/product/${id}`)
      .then((response) => {
        setProduct(response.data);
        setRatings(response.data.ratings || []); // Fetch the ratings data as well
        if (response.data.images && response.data.images.length > 0) {
          setCurrentImage(response.data.images[0].imageUrl);
        }
      })
      .catch((err) => {
        setError('Failed to load product details.');
        console.error(err);
      });
  }, [id]);

  // Handle Add to Cart
  const handleAddToCart = async () => {
    const token = localStorage.getItem('token'); // Retrieve JWT token

    if (!token) {
      setShowLoginPrompt(true); // Show login prompt modal
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cart/add?action=increase&productId=${id}&quantity=${quantity}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setMessage('Product added to cart successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to add product to cart.');
      console.error(error);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const closeLoginPrompt = () => {
    setShowLoginPrompt(false);
  };

  const redirectToLogin = () => {
    navigate('/login');
  };

  const redirectToReview = () => {
    const token = localStorage.getItem('token'); // Check if user is logged in
    if (!token) {
      setShowLoginPrompt(true); // Prompt to log in
    } else {
      navigate(`/write-review/${id}`); // Redirect to review page
    }
  };

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  if (!product) {
    return <div>Loading product details...</div>;
  }

  const discountedPrice =
    product.discount > 0
      ? (product.price - (product.price * product.discount) / 100).toFixed(2)
      : product.price.toFixed(2);

  // Render stars for averageRating
  const renderStars = (averageRating) => {
    const totalStars = 5;
    const filledStars = Math.floor(averageRating);
    const halfStar = averageRating - filledStars >= 0.5;
    const emptyStars = totalStars - filledStars - (halfStar ? 1 : 0);

    let stars = [];
    
    // Add filled stars
    for (let i = 0; i < filledStars; i++) {
      stars.push('★');
    }
    
    // Add half star if applicable
    if (halfStar) {
      stars.push('★');
    }
    
    // Add empty stars
    for (let i = 0; i < emptyStars; i++) {
      stars.push('☆');
    }

    return stars.join('');
  };

  // Render individual rating stars
  const renderRatingStars = (rating) => {
    const filledStars = Math.floor(rating);
    const halfStar = rating - filledStars >= 0.5;
    const emptyStars = 5 - filledStars - (halfStar ? 1 : 0);
    let stars = [];

    for (let i = 0; i < filledStars; i++) {
      stars.push('★');
    }
    if (halfStar) {
      stars.push('★');
    }
    for (let i = 0; i < emptyStars; i++) {
      stars.push('☆');
    }

    return stars.join('');
  };

  return (
    <div className="product-details-container">
      <div className="product-details-card">
        <div className="product-image-container-details">
          <div className="product-main-image">
            <img
              src={`/uploads/${currentImage}`}
              alt={product.name}
              className="product-detail-image"
            />
          </div>

          <div className="product-thumbnails">
            {product.images && product.images.length > 0 ? (
              product.images.map((image) => (
                <img
                  key={image.id}
                  src={`/uploads/${image.imageUrl}`}
                  alt={`Thumbnail ${image.id}`}
                  className="product-thumbnail"
                  onClick={() => setCurrentImage(image.imageUrl)}
                />
              ))
            ) : (
              <div>No images available</div>
            )}
          </div>
        </div>

        <div className="product-details-info">
          <h1>{product.name}</h1>

          <p>
            <strong>Price:</strong>
            <span className="original-price">₹{product.price}</span>
            {product.discount > 0 && (
              <span className="discounted-price"> ₹{discountedPrice}</span>
            )}
          </p>

          {product.discount > 0 && (
            <p><strong>Discount:</strong> {product.discount}% off</p>
          )}

          <p><strong>Color:</strong> {product.color}</p>
          <p><strong>Size:</strong> {product.size}</p>
          <p><strong>Description:</strong> {product.description}</p>
          <p><strong>Stock:</strong> {product.stock}</p>

          <div>
            <strong>Categories:</strong>
            <ul>
              {product.category.map((cat, index) => (
                <li key={index}>{cat.name}</li>
              ))}
            </ul>
          </div>

          {/* Average Rating */}
          <p>
            <strong>Rating:</strong> {product.averageRating.toFixed(1)}{' '}
            <span className="star-display">{renderStars(product.averageRating || 0)}</span> 
            <span className="rating-count">({product.noOfRating || 0} ratings)</span>
          </p>

          {/* Quantity Selector */}
          <div className="quantity-selector">
            <button onClick={() => setQuantity((prev) => Math.max(prev - 1, 1))}>-</button>
            <input
              type="number"
              value={quantity}
              min="1"
              max={product.stock}
              onChange={(e) =>
                setQuantity(
                  Math.max(1, Math.min(product.stock, e.target.value))
                )
              }
            />
            <button onClick={() => setQuantity((prev) => Math.min(prev + 1, product.stock))}>+</button>
          </div>

          <button className="add-to-cart-btn" onClick={handleAddToCart}>
            Add to Cart
          </button>

          {message && <div className="cart-message">{message}</div>}
        </div>
      </div>

      {/* Ratings Section */}
      <div className="product-ratings-section">
        <h2>Customer Ratings</h2>
        {ratings.length > 0 ? (
          ratings.map((rating, index) => (
            <div key={index} className="rating-card">
              <div className="rating-header">
                <span className="rating-name">{rating.name}</span>
                <span className="rating-date">{new Date(rating.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="rating-stars">{renderRatingStars(rating.rating)}</div>
              <p className="rating-comment">{rating.comment}</p>
            </div>
          ))
        ) : (
          <p>No ratings yet.</p>
        )}
      </div>

      {/* Review Button */}
      <div className="review-button-container">
        <button className="review-button" onClick={redirectToReview}>Add a Review</button>
      </div>

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p>Please log in to add a review.</p>
            <div className="modal-buttons">
              <button onClick={redirectToLogin}>Log In</button>
              <button onClick={closeLoginPrompt}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
